import React, { useEffect, useMemo } from 'react';
import * as Yup from 'yup';
import { Button, Col, Form, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { FormProvider, useForm } from 'react-hook-form';
import FormInput from '@/components/forms/FormInput';
import {
  GameRegistry,
  GameType,
  PokerVariant,
  TournamentInfoDto,
  TournamentRecurringType,
  TournamentStatus,
  TournamentUpdateDto,
} from '@repo/common-types';
import { ReactComponent as PeopleIcon } from '@/assets/icons/people.svg';
import { ReactComponent as TetherIcon } from '@/assets/tokens/tether-green.svg';
import usePayoutStructures from '@/hooks/usePayoutStructures';
import useBlindStructures from '@/hooks/useBlindStructures';
import checkVariants from '@/common/forms/table-handling/checkVariants';
import useGetPlayerTiers from '@/hooks/useGetPlayerTiers';
import { addMinutes } from 'date-fns';
import { yupResolver } from '@hookform/resolvers/yup';

// Local enum constants for tournament customization
const TournamentIcon = {
  NONE: 'NONE',
  PHNM_LOGO: 'PHNM_LOGO',
  STAR_LOGO: 'STAR_LOGO',
  BOUNTY_LOGO: 'BOUNTY_LOGO',
  DIAMOND_LOGO: 'DIAMOND_LOGO',
  TICKET_LOGO: 'TICKET_LOGO',
  FREE_LOGO: 'FREE_LOGO',
} as const;

const TournamentColor = {
  DEFAULT_WHITE: 'DEFAULT_WHITE',
  PURPLE: 'PURPLE',
  PINK: 'PINK',
  LIGHT_BLUE: 'LIGHT_BLUE',
  LIGHT_GREEN: 'LIGHT_GREEN',
  ORANGE: 'ORANGE',
  RED: 'RED',
  YELLOW: 'YELLOW',
  WATERMELON: 'WATERMELON',
  LEMON: 'LEMON',
} as const;

const getValidationSchema = ({ blindStructures, payoutStructures }) => {
  return Yup.object().shape({
    name: Yup.string().required('Name is required'),
    game: Yup.string().required('Game is required'),
    minPlayers: Yup.number().required('Minimum players is required').min(1, 'Minimum players must be greater than 1'),
    maxPlayers: Yup.number().required('Maximum players is required').min(1, 'Maximum players must be greater than 1'),
    buyIn: Yup.number().required('Buy in is required').min(0, 'Buy in must be greater than 0'),
    guaranteedPrize: Yup.number()
      .required('Guaranteed prize pool is required')
      .min(0, 'Guaranteed prize pool must be greater than 0'),
    registrationOpenPeriodInMinutes: Yup.number()
      .required('Registration open period is required')
      .min(0, 'Registration open period must be greater than 0'),
    lateRegistrationUpToLevel: Yup.number()
      .nullable()
      .required('Late registration period is required')
      .min(0, 'Late registration period must be greater than 0'),
    fee: Yup.number().required('Fee is required').min(0, 'Fee must be greater than 0'),
    payoutStructureId: Yup.string()
      .required('Payout structure is required')
      .test(
        'payout-structure-must-handle-max-players',
        'Payout structure must have enough payouts to handle the player count',
        (value, context) => {
          const selectedPayoutStructure = payoutStructures?.find((structure) => structure.id === value);
          const largestPayoutBracket = selectedPayoutStructure?.brackets?.reduce((acc, curr) => {
            if (curr.playersTo > acc) {
              acc = curr.playersTo;
            }
            return acc;
          }, 0);

          if (largestPayoutBracket && largestPayoutBracket < context?.parent?.maxPlayers) {
            return false;
          }

          return true;
        },
      ),
    tableSize: Yup.number().required('Table size is required').min(1, 'Table size must be greater than 1'),
    startingChips: Yup.number().required('Starting chips is required').min(0, 'Starting chips must be greater than 0'),
    blindIntervalInMinutes: Yup.number()
      .required('Blind interval is required')
      .min(0, 'Blind interval must be greater than 0'),
    blindStructureId: Yup.string()
      .required('Blind structure is required')
      .test(
        'blind-structure-has-bring-ins-if-stud-or-mixed',
        'Stud and mixed games require a blind structure with bring-ins',
        (value, context) => {
          if (context?.parent?.game && value) {
            const currentBlindStructure = blindStructures?.find((structure) => structure.id === value);
            const { stud, mixed } = checkVariants([context?.parent?.game as PokerVariant]);
            if (stud || mixed) {
              const validBlindStructureForVariant = currentBlindStructure?.levels?.every(
                (level) => !!level.bringIn && level.bringIn > 0,
              );
              if (!validBlindStructureForVariant) {
                return false;
              }
            }
          }
          return true;
        },
      ),
    maxAddons: Yup.number().required('Max addons is required').min(0, 'Max addons must be greater than 0'),
    maxReentries: Yup.number().required('Max reentries is required').min(0, 'Max reentries must be greater than 0'),
    bountyAmount: Yup.number().required('Bounty amount is required').min(0, 'Bounty amount must be greater than 0'),
    minimumTierRequired: Yup.string().nullable().optional(),
    startTime: Yup.date().nullable().optional(),
    payoutStructure: Yup.object().nullable().optional(),
    blindStructure: Yup.object().nullable().optional(),
    // Timebank validation fields
    startingTimebankInSeconds: Yup.number()
      .required('Starting timebank is required')
      .min(10, 'Starting timebank must be at least 10 seconds')
      .max(300, 'Starting timebank cannot exceed 300 seconds (5 minutes)'),
    timebankRefillAmountInSeconds: Yup.number()
      .required('Timebank refill amount is required')
      .min(10, 'Timebank refill amount must be at least 10 seconds')
      .max(120, 'Timebank refill amount cannot exceed 120 seconds'),
    timebankRefillEveryLevels: Yup.number()
      .required('Timebank refill frequency is required')
      .min(0, 'Timebank refill frequency must be 0 or greater (0 = disabled)')
      .max(50, 'Timebank refill frequency cannot exceed 50 levels'),
    startingDisconnectionTimebankInSeconds: Yup.number()
      .required('Starting disconnection timebank is required')
      .min(10, 'Starting disconnection timebank must be at least 10 seconds')
      .max(300, 'Starting disconnection timebank cannot exceed 300 seconds (5 minutes)'),
  });
};

const EditTournamentModalForm = ({
  onClose,
  selectedTournament,
  onUpdate,
  formStatus,
}: {
  onClose: () => void;
  selectedTournament: TournamentInfoDto;
  onUpdate: (data: Partial<TournamentUpdateDto>) => void;
  formStatus?: {
    isLoading: boolean;
    error: string | null;
  };
}) => {
  const { playerTiers } = useGetPlayerTiers();
  const { payoutStructures } = usePayoutStructures();
  const { blindStructures } = useBlindStructures();

  const form = useForm({
    resolver: yupResolver(
      getValidationSchema({
        blindStructures: [...(blindStructures ?? []), selectedTournament?.blindStructure],
        payoutStructures: [...(payoutStructures ?? []), selectedTournament?.payoutStructure],
      }),
    ),
    mode: 'onChange',
  });

  useEffect(() => {
    if (selectedTournament) {
      form.reset({
        ...selectedTournament,
        registrationOpenPeriodInMinutes: 12,
        lateRegistrationUpToLevel: selectedTournament.lateRegistrationUpToLevel ?? undefined,
      } as unknown as Partial<TournamentInfoDto>);
    }
  }, [selectedTournament, form]);

  const {
    handleSubmit,
    watch,
    formState: { dirtyFields, isDirty, errors },
    control,
  } = form;

  // State for the admin interface (total buy-in and rake amount)
  const [adminTotalBuyIn, setAdminTotalBuyIn] = React.useState(0);
  const [adminRakeAmount, setAdminRakeAmount] = React.useState(0);

  // Initialize admin values from existing tournament
  useEffect(() => {
    if (selectedTournament) {
      setAdminTotalBuyIn(selectedTournament.totalBuyin);
      setAdminRakeAmount(selectedTournament.fee ?? 0);
    }
  }, [selectedTournament]);

  const maxPlayers = watch('maxPlayers');
  useEffect(() => {
    if (Object.keys(form.formState.dirtyFields).includes('payoutStructureId')) {
      form.trigger('payoutStructureId');
    }
  }, [maxPlayers, form.formState.dirtyFields]);

  const game = watch('game');
  useEffect(() => {
    if (Object.keys(form.formState.dirtyFields).includes('blindStructureId')) {
      form.trigger('blindStructureId');
    }
  }, [game, form.formState.dirtyFields]);

  // Check if tournament is in a state where most fields should be disabled
  const tournamentStatus: TournamentStatus = selectedTournament?.status;
  const restrictedStatuses: TournamentStatus[] = [
    TournamentStatus.RUNNING,
    TournamentStatus.BREAK,
    TournamentStatus.PAUSED,
    TournamentStatus.COMPLETED,
    TournamentStatus.CANCELLED,
    TournamentStatus.INITIALIZING,
  ];
  const isRestrictedState: boolean = tournamentStatus && restrictedStatuses.includes(tournamentStatus);

  const onSubmit = (data: any) => {
    const dirtyValues: Partial<TournamentInfoDto> = Object.keys(dirtyFields).reduce((acc, key) => {
      acc[key] = data[key];
      return acc;
    }, {});

    const { lateRegistrationUpToLevel, payoutStructure, blindStructure, ...restValues } = dirtyValues;

    const updatePayload: Partial<TournamentUpdateDto> = {
      ...(restValues as TournamentUpdateDto),
    };

    if (lateRegistrationUpToLevel || dirtyValues.startTime || dirtyValues.blindIntervalInMinutes) {
      const lateRegistrationInMinutes =
        (lateRegistrationUpToLevel ?? selectedTournament?.lateRegistrationUpToLevel ?? 0) *
        (dirtyValues.blindIntervalInMinutes ?? selectedTournament?.blindIntervalInMinutes ?? 0);
      const lateRegistrationTime = addMinutes(
        dirtyValues.startTime ?? selectedTournament?.startTime ?? new Date(),
        lateRegistrationInMinutes,
      );

      updatePayload.lateRegistrationTime = lateRegistrationTime;
    }

    if (Object.keys(dirtyFields).includes('minimumTierRequired')) {
      if (dirtyValues.minimumTierRequired === '') {
        updatePayload.minimumTierRequired = null;
      }
    }

    if (Object.keys(dirtyFields).includes('recurringType')) {
      // if (dirtyValues.recurringType === ('None' as TournamentRecurringType)) {
      //   updatePayload.recurringType = null;
      // }
    }

    onUpdate(updatePayload);
  };

  const blindStructureId = watch('blindStructureId');
  // set using the blind structure levels
  const lateRegistrationOptions = useMemo(() => {
    if (!blindStructureId) {
      return [];
    }
    return selectedTournament?.blindStructure?.levels?.map((level) => {
      return {
        value: level.levelNumber,
        label: `Level ${level.levelNumber}`,
      };
    });
  }, [blindStructureId, blindStructures]);

  // Tournament customization options
  const iconOptions = [
    { value: TournamentIcon.NONE, label: 'None' },
    { value: TournamentIcon.PHNM_LOGO, label: 'PHNM Logo' },
    { value: TournamentIcon.STAR_LOGO, label: 'Star Logo' },
    { value: TournamentIcon.BOUNTY_LOGO, label: 'Bounty Logo' },
    { value: TournamentIcon.DIAMOND_LOGO, label: 'Diamond Logo' },
    { value: TournamentIcon.TICKET_LOGO, label: 'Ticket Logo' },
    { value: TournamentIcon.FREE_LOGO, label: 'Free Logo' },
  ];

  const colorOptions = [
    { value: TournamentColor.DEFAULT_WHITE, label: 'Default White' },
    { value: TournamentColor.PURPLE, label: 'Purple' },
    { value: TournamentColor.PINK, label: 'Pink' },
    { value: TournamentColor.LIGHT_BLUE, label: 'Light Blue' },
    { value: TournamentColor.LIGHT_GREEN, label: 'Light Green' },
    { value: TournamentColor.ORANGE, label: 'Orange' },
    { value: TournamentColor.RED, label: 'Red' },
    { value: TournamentColor.YELLOW, label: 'Yellow' },
    { value: TournamentColor.WATERMELON, label: 'Watermelon' },
    { value: TournamentColor.LEMON, label: 'Lemon' },
  ];

  return (
    <FormProvider {...form}>
      <Modal isOpen toggle={onClose} id="edit-tournament-modal" className="modal-lg" centered>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>Edit Tournament Details</ModalHeader>
          <ModalBody style={{ maxHeight: '75vh' }} className="overflow-auto">
            <div className="w-100 d-flex flex-column justify-content-start max-h-[75vh] gap-2 overflow-auto">
              <div className="w-100 d-flex justify-content-between flex-row gap-2">
                <Col>
                  {/* Existing form fields */}
                  <Row className="w-100">
                    <Col>
                      <FormInput control={control} label="Tournament Name" name="name" />
                    </Col>
                  </Row>

                  <Row className="w-100">
                    <Col>
                      <FormInput control={control} label="Tournament Name" name="name" disabled={isRestrictedState} />
                    </Col>
                    <Col>
                      <FormInput
                        control={control}
                        name="startTime"
                        label="Start Time"
                        type="datetime-local"
                        disabled={isRestrictedState}
                      />
                    </Col>
                  </Row>
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="game"
                        label="Game Type"
                        type="select"
                        options={Object.values(GameType)
                          .filter((type) => {
                            const gameInfo = GameRegistry[type];
                            return !gameInfo?.capped;
                          })
                          .map((type) => ({ value: type, label: type }))}
                        disabled={true}
                      />
                    </Col>
                  </Row>
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="minPlayers"
                        label="Minimum Players"
                        type="number"
                        Icon={PeopleIcon}
                        disabled={true}
                      />
                    </Col>
                    <Col>
                      <FormInput
                        control={control}
                        name="maxPlayers"
                        label="Maximum Players"
                        type="number"
                        Icon={PeopleIcon}
                        disabled={true}
                      />
                    </Col>
                  </Row>
                  {/* Modified Buy-in and Rake Section - Always Disabled */}
                  <Row className="w-100">
                    <Col>
                      <label className="form-label">Buy-In</label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <TetherIcon width="16" height="16" />
                        </span>
                        <input
                          type="number"
                          className="form-control"
                          value={adminTotalBuyIn}
                          disabled={true}
                          step="0.01"
                          readOnly
                        />
                      </div>
                      <small className="text-muted">Prize pool amount (cannot be modified)</small>
                    </Col>
                    <Col>
                      <label className="form-label">Rake/Fee</label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <TetherIcon width="16" height="16" />
                        </span>
                        <input
                          type="number"
                          className="form-control"
                          value={adminRakeAmount}
                          disabled={true}
                          step="0.01"
                          readOnly
                        />
                      </div>
                      <small className="text-muted">Fee amount (cannot be modified)</small>
                    </Col>
                  </Row>
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="guaranteedPrize"
                        label="Guarantee Prize Pool"
                        type="number"
                        Icon={TetherIcon}
                        disabled={true}
                      />
                    </Col>
                  </Row>
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="maxAddons"
                        label="Max Addons"
                        type="number"
                        allowDecimal={false}
                        disabled={true}
                      />
                    </Col>
                  </Row>
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="maxReentries"
                        label="Max Reentries"
                        type="select"
                        includeEmptyOption={false}
                        options={[
                          {
                            value: 0,
                            label: '0',
                          },
                          {
                            value: 1,
                            label: '1',
                          },
                          {
                            value: 2,
                            label: '2',
                          },
                          {
                            value: 3,
                            label: '3',
                          },
                          {
                            value: 5,
                            label: '5',
                          },
                          {
                            value: 10,
                            label: '10',
                          },
                          {
                            value: 9999,
                            label: 'Unlimited',
                          },
                        ]}
                        disabled
                      />
                    </Col>
                    <Col>
                      <FormInput
                        control={control}
                        name="bountyAmount"
                        label="Bounty Amount"
                        type="number"
                        disabled={true}
                      />
                    </Col>
                  </Row>
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="payoutStructureId"
                        label="Payout Structure"
                        type="select"
                        options={[
                          ...(payoutStructures
                            ?.filter((structure) => structure.name !== selectedTournament?.payoutStructure?.name)
                            .map((structure) => ({ value: structure.id, label: structure.name })) ?? []),
                          {
                            value: selectedTournament?.payoutStructure?.id,
                            label: selectedTournament?.payoutStructure?.name,
                          },
                        ]}
                        disabled={isRestrictedState}
                      />
                    </Col>
                    <Col>
                      <FormInput
                        control={control}
                        name="blindStructureId"
                        label="Blind Structure"
                        type="select"
                        options={[
                          ...(blindStructures
                            ?.filter((structure) => structure.name !== selectedTournament?.blindStructure?.name)
                            .map((structure) => ({ value: structure.id, label: structure.name })) ?? []),
                          {
                            value: selectedTournament?.blindStructure?.id,
                            label: selectedTournament?.blindStructure?.name,
                          },
                        ]}
                        disabled={isRestrictedState}
                      />
                    </Col>
                  </Row>
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="lateRegistrationUpToLevel"
                        label="Late Registration Period (up to level)"
                        type="select"
                        options={lateRegistrationOptions}
                        valueAsNumber
                        includeEmptyOption={false}
                        disabled={isRestrictedState}
                      />
                    </Col>
                  </Row>
                  <Row className="w-100 border-top border-secondary border-white py-3 opacity-25" />
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="blindIntervalInMinutes"
                        label="Blind Duration (mins)"
                        type="number"
                        allowDecimal={false}
                        valueAsNumber
                        disabled={isRestrictedState}
                      />
                    </Col>
                  </Row>
                  {/* Timebank Configuration Section */}
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="startingTimebankInSeconds"
                        label="Starting Timebank (seconds)"
                        type="number"
                        allowDecimal={false}
                        valueAsNumber
                        disabled={isRestrictedState}
                        placeholder="15"
                      />
                    </Col>
                    <Col>
                      <FormInput
                        control={control}
                        name="startingDisconnectionTimebankInSeconds"
                        label="Disconnection Timebank (seconds)"
                        type="number"
                        allowDecimal={false}
                        valueAsNumber
                        disabled={isRestrictedState}
                        placeholder="30"
                      />
                    </Col>
                  </Row>
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="timebankRefillAmountInSeconds"
                        label="Timebank Refill Amount (seconds)"
                        type="number"
                        allowDecimal={false}
                        valueAsNumber
                        disabled={isRestrictedState}
                        placeholder="30"
                      />
                    </Col>
                    <Col>
                      <FormInput
                        control={control}
                        name="timebankRefillEveryLevels"
                        label="Refill Every X Levels (0 = disabled)"
                        type="number"
                        allowDecimal={false}
                        valueAsNumber
                        disabled={isRestrictedState}
                        placeholder="10"
                      />
                    </Col>
                  </Row>
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="tableSize"
                        label="How Many Handed"
                        type="select"
                        valueAsNumber
                        options={Array.from({ length: 12 }, (_, i) => ({ value: i + 2, label: `${i + 2} Handed` }))}
                        disabled={isRestrictedState}
                      />
                    </Col>
                    <Col>
                      <FormInput
                        control={control}
                        name="startingChips"
                        label="Starting Chips Stack"
                        type="number"
                        disabled={isRestrictedState}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <FormInput
                        control={control}
                        name="minimumTierRequired"
                        label="Min Tier Required"
                        type="select"
                        options={playerTiers?.map((tier) => ({
                          value: tier.id,
                          label: tier.name,
                        }))}
                        disabled={isRestrictedState}
                      />
                    </Col>
                    <Col>
                      <FormInput
                        control={control}
                        name="recurringType"
                        label="Recurring Type"
                        type="select"
                        includeEmptyOption={false}
                        options={[
                          { value: null, label: 'None' },
                          ...Object.values(TournamentRecurringType)
                            .filter((type) => type !== TournamentRecurringType.NONE)
                            .map((type) => ({
                              value: type,
                              label: type,
                            })),
                        ]}
                        disabled={isRestrictedState}
                      />
                    </Col>
                  </Row>

                  {/* Tournament Customization Section */}
                  <Row>
                    <Col>
                      <FormInput
                        control={control}
                        name="icon"
                        label="Tournament Icon"
                        type="select"
                        options={iconOptions}
                      />
                    </Col>
                    <Col>
                      <FormInput
                        control={control}
                        name="titleColor"
                        label="Title Color"
                        type="select"
                        options={colorOptions}
                      />
                    </Col>
                  </Row>
                  <Row className="w-100">
                    <Col></Col>
                  </Row>
                </Col>
              </div>
              {formStatus?.error && (
                <div className="text-danger mt-2" role="alert">
                  {formStatus?.error}
                </div>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="grey" onClick={onClose} disabled={formStatus?.isLoading}>
              Cancel
            </Button>
            <Button disabled={!isDirty || Object.keys(errors).length > 0} type="submit" color="primary">
              {formStatus?.isLoading ? 'Saving...' : 'Save'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </FormProvider>
  );
};

export default EditTournamentModalForm;
